import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, FileItem, FileItemDetails, FileItemInfo, FileItemPreview, FileItemProgress, FileList } from './styles';
import { v4 as uuidv4, v4 } from "uuid";
import { IApplicationState } from '../../store';
import { IUploadedInfo } from '../../store/system/types';
import ErrorMessage from '../ErrorMessage';
import { fileUpload } from '../../store/photos/slice';
import { useTranslation } from 'react-i18next';
import { fileUploadVideo } from '../../store/videos/slices';

interface Props {
    entityId: string;
    type: "image" | "video" | "imageFlir";
    callback?: (uploaded: IUploadedInfo) => void;
}


const FileUploader: React.FC<Props> = ({ entityId, callback, type }) => {
    const { t } = useTranslation();

    const containerRef = useRef<HTMLDivElement>(null);

    const inputRef = useRef<HTMLInputElement>(null);

    const uploads = useSelector((state: IApplicationState) => state.photos.uploads.filter(upload => upload.entityId === entityId))

    const [fileIds, setFileIds] = useState<string[]>([])

    const [inputFileId] = useState(v4())

    const dispatch = useDispatch();

    const handleDragEnter = useCallback((event: DragEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const element = event.currentTarget as HTMLDivElement;

        element.classList.add('highlight')
    }, [])

    const handleDragLeave = useCallback((event: DragEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const element = event.currentTarget as HTMLDivElement;

        element.classList.remove('highlight')
    }, [])

    const handleDragOver = useCallback((event: DragEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const element = event.currentTarget as HTMLDivElement;

        element.classList.add('highlight')
    }, [])


    const handleFiles = useCallback((files?: FileList | null) => {

        const ids: string[] = [];

        if (files) {

            for (let i = 0; i < files.length; i++) {
                const file = files.item(i);

                if (file) {
                    const formData = new FormData();
                    const id = uuidv4();

                    ids.push(id);

                    formData.append('file', file);
                    switch (type) {
                        case 'image':
                            dispatch(fileUpload({
                                id,
                                entityId,
                                file,
                                data: formData,
                                callback
                            }))
                            break;
                        case 'imageFlir':
                            dispatch(fileUpload({
                                id,
                                entityId,
                                file,
                                data: formData,
                                callback
                            }))
                            break;
                        case 'video':
                            dispatch(fileUploadVideo({
                                id,
                                entityId,
                                file,
                                data: formData,
                                callback
                            }))
                            break;
                        default:
                            break;
                    }
                }
            }

            setFileIds([...fileIds, ...ids])
        }
    }, [fileIds, type, dispatch, entityId, callback])

    const handleDrop = useCallback((event: DragEvent) => {

        event.preventDefault();
        event.stopPropagation();

        const element = event.currentTarget as HTMLDivElement;

        element.classList.remove('highlight');

        const dataTrasnfer = event.dataTransfer;

        const files = dataTrasnfer?.files;

        handleFiles(files);
    }, [handleFiles])

    useEffect(() => {
        let ref = containerRef.current;

        if (ref) {
            ref.addEventListener('dragenter', handleDragEnter)
            ref.addEventListener('dragleave', handleDragLeave)
            ref.addEventListener('dragover', handleDragOver)
            ref.addEventListener('drop', handleDrop)
        }

        return () => {
            ref?.removeEventListener('dragenter', handleDragEnter);
            ref?.removeEventListener('dragleave', handleDragLeave);
            ref?.removeEventListener('dragover', handleDragOver);
            ref?.removeEventListener('drop', handleDrop);
        }
    }, [handleDragEnter, handleDragLeave, handleDrop, handleDragOver])

    useEffect(() => {
        let ref = inputRef.current

        const tmpListener = (event: Event) => {
            const element = event.target as HTMLInputElement;

            const files = element.files;

            handleFiles(files)
        }

        if (ref) {
            ref.addEventListener("change", tmpListener)
        }

        return () => {
            ref?.removeEventListener("change", tmpListener)
        }
    }, [handleFiles])

    return <Container ref={containerRef}>
        <p>{t("pages.rooms.dialogs.file_upload")}</p>

        <input ref={inputRef} type="file" id={inputFileId} multiple accept="image/*, video/*, .signals"></input>

        <label className="fileUploadButton" htmlFor={inputFileId}>{t("pages.rooms.dialogs.select_file")}</label>

        {uploads.length > 0 && <FileList>
            {uploads?.filter(upload => fileIds.includes(upload.id)).map(upload => <FileItem key={`upload-id-${upload.id}`}>
                <FileItemPreview />

                <FileItemInfo>
                    <FileItemDetails>
                        {upload.file?.name}
                    </FileItemDetails>
                    {upload.isSaving && <p>Saving....</p>}
                    {!upload.isSaving && !upload.error && <FileItemProgress max={100} value={upload.percentage} />}
                    {!upload.isSaving && upload.error && <ErrorMessage>{upload.error.message}</ErrorMessage>}
                </FileItemInfo>
            </FileItem>)}
        </FileList>}
    </Container>
}

export default (FileUploader);